import React, { useState, useEffect, useRef } from 'react';

import AllVariants from 'shared/layouts/Product/AllShades/View';
import { PageLocationType } from 'shared/store/categoryListing/types';
import { isSize } from './helpers';
import { CartData } from 'store/addToCart/types';
import { DEFAULT_LANGUAGE } from '@nykaa/intl-shared/constants';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'store/types';
import { PageJourney } from 'store/dataLayer/action';
import { getEligibleForVariant } from 'shared/helpers/experimentV2';
import { AB_SIZE_MINI_PRODUCT } from 'shared/constants/experiments';
import { pushData, pushEvent } from '@nykaa/data-layer';
import {
  PRODUCT_ADDED_TO_CART,
  SHADE_CLICKED_EVENT,
  SIZE_CLICKED_EVENT,
  standardErrorMessages,
} from 'shared/constants';
import PLPVariantSelector from '@nykaa/variant-selector/PLPVariantSelector';
import {
  ItemsObject,
  ProductSizeData,
} from '@nykaa/variant-selector/PLPVariantSelector/types';
import {
  BulkAddToCartParams,
  doBulkAddToCartAndRefresh,
} from 'store/addToCart/action';
import { useStringFromId } from '@nykaa/intl-shared';
import { ADDED_TO_CART } from 'constants/strings';
import {
  fireAddToCartEvent,
  fireStepperQuantityUpdateTracking,
  getViewDetailsQueryParam,
} from './tracking';

interface AllVariantProps {
  product: any;
  variantsMap: { [productId: string]: any[] }; // TODO: fix this when we define the object for Variants in recommendation/listing
  onCloseHandler: Function;
  fetchVariants: Function;
  handleAddToCartTracking: (cartData: CartData) => void;
  pageLocation: PageLocationType;
  lang?: string;
  clickedFrom?: string;
  newCtaText?: string;
  isGoToBag?: boolean;
  setNewCtaText: (prop: string) => void;
  setIsGoToBag: (prop: boolean) => void;
  journeyData: PageJourney;
  priveBand: string;
  notchHeight?: number;
  handlePostAddToCart?: () => void;
  isConvSearch?: boolean;
  gptConversationId?: string;
  appTrackingKeys?: { [key: string]: any };
  isLoading?: boolean;
  handleCardClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    extraQueryParams?: string
  ) => void;
  setFullScreenLoader: (value: boolean) => void;
  setAddToBagError: (value: boolean) => void;
  setAddToBagMessage: (value: string) => void;
  isPlpVariantSelectorFashionEnabled?: boolean;
  isPlpVariantSelectorGroomingEnabled?: boolean;
  productSizeData?: { [productId: string]: ProductSizeData };
  items?: ItemsObject[];
  setItems?: (items: ItemsObject[]) => void;
  isFetchingError?: { [productId: string]: boolean };
}

interface VariantChange {
  id: string;
  contentLang: {
    [key: string]: string;
  };
}

const AllVariant = ({
  product,
  onCloseHandler,
  variantsMap,
  fetchVariants,
  handleAddToCartTracking,
  pageLocation,
  lang,
  clickedFrom,
  isGoToBag,
  setIsGoToBag,
  newCtaText,
  setNewCtaText,
  journeyData,
  priveBand,
  notchHeight,
  handlePostAddToCart,
  isConvSearch,
  gptConversationId,
  appTrackingKeys,
  isLoading,
  handleCardClick,
  setFullScreenLoader,
  setAddToBagError,
  setAddToBagMessage,
  isPlpVariantSelectorFashionEnabled,
  isPlpVariantSelectorGroomingEnabled,
  productSizeData,
  items,
  setItems,
  isFetchingError,
}: AllVariantProps) => {
  const ITEM_ADDED_TO_CART = useStringFromId(
    ADDED_TO_CART,
    PRODUCT_ADDED_TO_CART
  );
  // TODO: fix the type for variant data - a list of Listing Variants
  const [variantData, setVariantData] = useState<any[]>([]);
  const [selectedVariant, setSelectedVariant] = useState({});
  const { isExperimentEnabled, lang: experimentLanguage } = useSelector(
    (state: State) => state.langConfig
  );
  const dispatch = useDispatch();
  const { isPro } = useSelector((state: State) => state.auth.user);
  const isMiniProductEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_SIZE_MINI_PRODUCT, 'A')
  );
  const sizeUnit = useSelector(
    (state: State) => state.remoteConfig.configs.sizeUnit
  );

  const { id } = product || {};

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Reset the ref when id changes
    hasFetchedRef.current = false;
  }, [id]);

  useEffect(() => {
    // Fetch if: no data exists for this id, OR there was an error previously (user is retrying by reopening the sheet)
    const hasValidData = variantsMap[id]?.length > 0;

    if (hasValidData) {
      // Use cached data
      const cached = variantsMap[id];
      let firstVariant = cached[0];
      if (firstVariant?.defaultPid) {
        firstVariant = cached.find((item) => item.childId === item.defaultPid);
        if (!firstVariant) firstVariant = cached[0];
      }
      setSelectedVariant(firstVariant);
      setVariantData(cached);
      return;
    }

    // Skip fetch if we've already attempted on this mount
    if (hasFetchedRef.current) {
      return;
    }

    if (id) {
      hasFetchedRef.current = true;
      let params = {
        id,
        priveBand,
      };
      if (isExperimentEnabled && experimentLanguage !== DEFAULT_LANGUAGE) {
        params.lang = experimentLanguage;
      }

      fetchVariants(params).then((response: any) => {
        if (response) {
          let firstVariant = response[0];
          if (firstVariant?.defaultPid) {
            firstVariant = response.find(
              (item) => item.childId === item.defaultPid
            );
            if (!firstVariant) firstVariant = response[0];
          }
          setSelectedVariant(firstVariant);
          setVariantData(response);
        }
      });
    }
  }, [
    fetchVariants,
    variantsMap,
    id,
    isExperimentEnabled,
    experimentLanguage,
    priveBand,
  ]);

  useEffect(() => () => pushData({ cardClickAction: '' }), []);

  const isVariantSelectionEnabled =
    isPlpVariantSelectorFashionEnabled || isPlpVariantSelectorGroomingEnabled;

  if (
    !isVariantSelectionEnabled &&
    (!variantData || variantData.length === 0)
  ) {
    return null;
  }

  const {
    showBestSeller,
    brandName,
    parentId,
    variantType,
    primaryCategories,
    name,
  } = product;

  const handleVariantChange = (newVarient: VariantChange) => {
    const { id: variantId } = newVarient;
    let variantIndex = variantData.findIndex(
      (variant) => variant.childId === variantId
    );
    if (variantIndex < 0) {
      variantIndex = 0;
    }
    setSelectedVariant(variantData[variantIndex]);
    let variantClickEvent = isSize(variantType)
      ? SIZE_CLICKED_EVENT
      : SHADE_CLICKED_EVENT;
    pushEvent(variantClickEvent, {
      selectedVariant: { ...product, ...selectedVariant },
    });
  };

  const handleClickAddToBag = async (data: ItemsObject[]) => {
    let params: BulkAddToCartParams = {
      isPro,
      items: data,
    };
    try {
      onCloseHandler();
      setFullScreenLoader(true);
      const cartData = (await dispatch(
        doBulkAddToCartAndRefresh(params) as any
      )) as CartData;
      fireAddToCartEvent(product, cartData);
      setAddToBagError(false);
      setAddToBagMessage(ITEM_ADDED_TO_CART);
    } catch (error: any) {
      setAddToBagError(true);
      if (error.errorInApi) {
        setAddToBagMessage(error.message);
      } else {
        setAddToBagMessage(standardErrorMessages.SOMETHING_WRONG);
      }
    } finally {
      setFullScreenLoader(false);
      setItems && setItems([]);
    }
  };

  const handleQuantityChange = (
    childId: string,
    originalQuantity: number,
    updatedQuantity: number,
    userAction: 'increase' | 'decrease'
  ) => {
    fireStepperQuantityUpdateTracking({
      product,
      variants: variantData,
      childId,
      originalQuantity,
      updatedQuantity,
      userAction,
    });
  };

  const selectedVariantWithParentButtonText = {
    ...selectedVariant,
    buttonText: product.buttonText,
    pageSource: product?.pageSource,
    trackingParams: product?.trackingParams,
    disablePriveProduct: product?.disablePriveProduct,
    variants: variantData,
    parentId: parentId || id,
    brandName,
    type: product.type,
    variantType,
  };
  return isVariantSelectionEnabled ? (
    <PLPVariantSelector
      brandName={brandName}
      parentId={parentId}
      product={selectedVariantWithParentButtonText as any}
      variants={variantData}
      isLoading={isLoading}
      variantType={variantType}
      isFashionCategory={isPlpVariantSelectorFashionEnabled}
      onCloseHandler={onCloseHandler}
      name={name}
      handleAddToBag={handleClickAddToBag}
      handleViewDetails={(e: React.MouseEvent<HTMLButtonElement>) => {
        handleCardClick(e, getViewDetailsQueryParam(variantType));
      }}
      productSizeData={productSizeData?.[id] || {}}
      sizeUnit={sizeUnit}
      items={items}
      setItems={setItems}
      onQuantityChange={handleQuantityChange}
      isError={isFetchingError?.[id]}
    />
  ) : (
    <AllVariants
      isSize={isSize(variantType)}
      changeVariant={handleVariantChange}
      product={selectedVariantWithParentButtonText}
      onCloseHandler={onCloseHandler}
      showBestSeller={showBestSeller}
      variants={variantData}
      brandName={brandName}
      parentId={parentId}
      handleAddToCartTracking={handleAddToCartTracking}
      primaryCategories={primaryCategories}
      pageLocation={pageLocation}
      lang={lang}
      clickedFrom={clickedFrom}
      newCtaText={newCtaText}
      isGoToBag={isGoToBag}
      setIsGoToBag={setIsGoToBag}
      setNewCtaText={setNewCtaText}
      journeyData={journeyData}
      isMiniEnabled={isMiniProductEnabled}
      notchHeight={notchHeight}
      handlePostAddToCart={handlePostAddToCart}
      isConvSearch={isConvSearch}
      gptConversationId={gptConversationId}
      appTrackingKeys={appTrackingKeys}
    />
  );
};

export default AllVariant;
